import * as React from 'react'
import {
  Play,
  Square,
  ChevronDown,
  CheckCircle2,
  XCircle,
  X,
  Loader2,
  Target,
  BarChart3,
  Activity,
  Settings2,
  Zap,
  Info,
  FileText,
  TrendingUp,
  RotateCcw,
  Sliders,
} from 'lucide-react'
import { DataGenerator, type DataGeneratorMeta } from '@/components/DataGenerator'
import type { InputFormat } from '@/types'
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  Cell,
} from 'recharts'
import { cn } from '@/lib/utils'
import { usePersistStore, defaultTunableBiasParams } from '@/stores/persistStore'
import { useWorker } from '@/lib/useWorker'
import { DEFAULT_TUNABLE_BIAS_PARAMS, TUNABLE_BIAS_TEST_NAMES } from '@/lib/constants'
import { FileUploader } from '@/components/FileUploader'
import { ImportDataBanner } from '@/components/ImportDataBanner'
import { ExportButton } from '@/components/ExportButton'
import { ChartExportButton } from '@/components/ChartExportButton'
import { ResultSummaryBar } from '@/components/ResultSummaryBar'
import { SnapshotManager } from '@/components/SnapshotManager'
import { DataExportMenu } from '@/components/DataExportMenu'
import { SectionHeader } from '@/components/SectionHeader'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import { Progress } from '@/components/ui/progress'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { APP_VERSION } from '@/lib/constants'
import type {
  TunableBiasParameters,
  TunableBiasSuiteResult,
  TunableBiasTestResult,
  BiasAccuracyResult,
  BiasStabilityResult,
  GeneralizedRunsResult,
  GeneralizedSerialResult,
  AutocorrelationResult,
  ConditionalEntropyResult,
  GeneralizedApEnResult,
  SpectralAnalysisResult,
  DwellTimeResult,
  ExportReport,
} from '@/types'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatPValue(p: number): string {
  if (p < 0.0001) return p.toExponential(3)
  return p.toFixed(6)
}

function pValueColor(p: number): string {
  if (p >= 0.01) return 'text-green-600 dark:text-green-400'
  if (p >= 0.001) return 'text-yellow-600 dark:text-yellow-400'
  return 'text-red-600 dark:text-red-400'
}

function biasLabel(p: number): string {
  if (Math.abs(p - 0.5) < 0.001) return 'Unbiased'
  if (p < 0.3 || p > 0.7) return 'Heavy bias'
  return 'Light bias'
}

function biasColor(p: number): string {
  if (Math.abs(p - 0.5) < 0.001) return 'text-blue-500'
  if (p < 0.3 || p > 0.7) return 'text-orange-500'
  return 'text-yellow-500'
}

/** Extract a "passed" bool from any test result */
function testPassed(t: TunableBiasTestResult): boolean {
  switch (t.testName) {
    case 'Bias Accuracy':
      return (t as BiasAccuracyResult).passed
    case 'Bias Stability':
      return (t as BiasStabilityResult).homogeneity.passed && (t as BiasStabilityResult).trend.passed
    case 'Generalized Runs': {
      const r = t as GeneralizedRunsResult
      return r.onesRuns.passed && r.zerosRuns.passed
    }
    case 'Generalized Serial':
      return (t as GeneralizedSerialResult).passed
    case 'Autocorrelation': {
      const a = t as AutocorrelationResult
      return a.ljungBox.every((lb) => lb.passed)
    }
    case 'Conditional Entropy':
      return true // informational — no pass/fail
    case 'Generalized Approximate Entropy': {
      const g = t as GeneralizedApEnResult
      return g.passed.every(Boolean)
    }
    case 'Spectral Analysis':
      return true // informational — no binary pass/fail
    case 'Dwell Time':
      return true // informational — no binary pass/fail
    default:
      return true
  }
}

/** Primary statistic for a test result */
function primaryStatistic(t: TunableBiasTestResult): number {
  switch (t.testName) {
    case 'Bias Accuracy':
      return (t as BiasAccuracyResult).zStatistic
    case 'Bias Stability':
      return (t as BiasStabilityResult).homogeneity.chiSquare
    case 'Generalized Runs':
      return (t as GeneralizedRunsResult).onesRuns.chiSquare
    case 'Generalized Serial':
      return (t as GeneralizedSerialResult).chiSquare
    case 'Autocorrelation':
      return (t as AutocorrelationResult).ljungBox[0]?.Q ?? 0
    case 'Conditional Entropy':
      return (t as ConditionalEntropyResult).entropies[0] ?? 0
    case 'Generalized Approximate Entropy':
      return (t as GeneralizedApEnResult).observedPhi[0] ?? 0
    case 'Spectral Analysis':
      return (t as SpectralAnalysisResult).spectralExponent
    case 'Dwell Time':
      return (t as DwellTimeResult).switchingRate
    default:
      return 0
  }
}

/** Primary p-value for a test result */
function primaryPValue(t: TunableBiasTestResult): number | null {
  switch (t.testName) {
    case 'Bias Accuracy':
      return (t as BiasAccuracyResult).pValue
    case 'Bias Stability':
      return (t as BiasStabilityResult).homogeneity.pValue
    case 'Generalized Runs':
      return (t as GeneralizedRunsResult).onesRuns.pValue
    case 'Generalized Serial':
      return (t as GeneralizedSerialResult).pValue
    case 'Autocorrelation':
      return (t as AutocorrelationResult).ljungBox[0]?.pValue ?? null
    case 'Conditional Entropy':
      return null // no single p-value
    case 'Generalized Approximate Entropy':
      return (t as GeneralizedApEnResult).pValues[0] ?? null
    case 'Spectral Analysis':
      return (t as SpectralAnalysisResult).pValue
    case 'Dwell Time':
      return null // no single p-value
    default:
      return null
  }
}

// ---------------------------------------------------------------------------
// P-Value Summary Chart (log scale scatter)
// ---------------------------------------------------------------------------

const TB_SHORT_NAMES: Record<string, string> = {
  'Bias Accuracy': 'BiasAcc',
  'Bias Stability': 'Stabil',
  'Generalized Runs': 'GenRun',
  'Generalized Serial': 'GenSer',
  'Autocorrelation': 'ACF',
  'Conditional Entropy': 'CondH',
  'Generalized Approximate Entropy': 'GenApEn',
  'Spectral Analysis': 'Spectr',
  'Dwell Time': 'Dwell',
}

/** Tests that are informational only (no pass/fail p-value) */
const INFORMATIONAL_TESTS = new Set(['Conditional Entropy', 'Dwell Time'])

interface TBPValueChartProps {
  tests: TunableBiasTestResult[]
  alpha: number
}

function TBPValueChart({ tests, alpha }: TBPValueChartProps) {
  const chartRef = React.useRef<HTMLDivElement>(null)

  const isDark = document.documentElement.classList.contains('dark')
  const colors = {
    muted: isDark ? '#a1a1aa' : '#71717a',
    border: isDark ? '#3f3f46' : '#e4e4e7',
  }

  const plotData = tests.map((t, i) => {
    const pVal = primaryPValue(t)
    const isInfo = INFORMATIONAL_TESTS.has(t.testName)
    const passed = testPassed(t)
    return {
      x: i,
      name: TB_SHORT_NAMES[t.testName] ?? t.testName.substring(0, 6),
      fullName: t.testName,
      pValue: isInfo ? 1 : Math.max(pVal ?? 1e-10, 1e-10),
      passed,
      isInfo,
    }
  })

  const CustomTooltip = ({ active, payload }: any) => {
    if (!active || !payload?.[0]) return null
    const d = payload[0].payload
    return (
      <div className="rounded-md border bg-popover px-3 py-2 text-xs shadow-md">
        <p className="font-medium">{d.fullName}</p>
        {d.isInfo ? (
          <p className="text-muted-foreground mt-0.5">Informational (no p-value)</p>
        ) : (
          <p className="font-mono mt-0.5">p = {d.pValue < 0.0001 ? d.pValue.toExponential(3) : d.pValue.toFixed(6)}</p>
        )}
        <p className={d.isInfo ? 'text-muted-foreground' : d.passed ? 'text-green-500' : 'text-red-500'}>
          {d.isInfo ? 'INFO' : d.passed ? 'PASS' : 'FAIL'}
        </p>
      </div>
    )
  }

  const yTicks = [1, 0.1, 0.01, 0.001, 0.0001]

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium">
            P-Value Summary <span className="text-[10px] font-normal text-muted-foreground ml-1">(log scale)</span>
          </CardTitle>
          <ChartExportButton containerRef={chartRef} fileName="tunable-bias-pvalues" />
        </div>
      </CardHeader>
      <CardContent className="pb-4 px-2 sm:px-4">
        <div ref={chartRef}>
          <div className="h-[240px]">
            <ResponsiveContainer width="100%" height="100%">
              <ScatterChart margin={{ top: 12, right: 12, bottom: 50, left: 4 }}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke={colors.border}
                  opacity={0.5}
                  horizontal
                  vertical={false}
                />
                <XAxis
                  dataKey="x"
                  type="number"
                  domain={[-0.5, plotData.length - 0.5]}
                  ticks={plotData.map((_, i) => i)}
                  tickFormatter={(i: number) => plotData[i]?.name ?? ''}
                  tick={{ fontSize: 9, fill: colors.muted }}
                  tickLine={false}
                  axisLine={{ stroke: colors.border }}
                  angle={-40}
                  textAnchor="end"
                  interval={0}
                />
                <YAxis
                  dataKey="pValue"
                  type="number"
                  scale="log"
                  domain={[
                    Math.min(0.0001, ...plotData.filter(d => !d.isInfo).map(d => d.pValue)) / 2,
                    1,
                  ]}
                  ticks={yTicks}
                  tickFormatter={(v: number) => v >= 0.01 ? v.toString() : v.toExponential(0)}
                  tick={{ fontSize: 9, fill: colors.muted }}
                  tickLine={false}
                  axisLine={false}
                  width={48}
                />
                <ReferenceLine
                  y={alpha}
                  stroke="#ef4444"
                  strokeDasharray="6 3"
                  strokeWidth={2}
                  label={{
                    value: `\u03B1 = ${alpha}`,
                    position: 'insideBottomLeft',
                    style: { fontSize: 9, fill: '#ef4444', fontWeight: 600 },
                    offset: 4,
                  }}
                />
                <RechartsTooltip content={<CustomTooltip />} />
                <Scatter data={plotData} isAnimationActive={false}>
                  {plotData.map((entry, i) => (
                    <Cell
                      key={i}
                      fill={entry.isInfo ? '#71717a' : entry.passed ? '#22c55e' : '#ef4444'}
                      r={6}
                    />
                  ))}
                </Scatter>
              </ScatterChart>
            </ResponsiveContainer>
          </div>
        </div>
        <p className="text-[10px] text-muted-foreground mt-1 px-2">
          Each dot is one test's p-value (log scale). Dots below {'\u03B1'} = {alpha} indicate failure.
          Gray dots are informational tests without a binary pass/fail.
        </p>
      </CardContent>
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Specialized Detail Components
// ---------------------------------------------------------------------------

function BiasAccuracyDetails({ result }: { result: BiasAccuracyResult }) {
  const ciLow = result.confidenceInterval[0]
  const ciHigh = result.confidenceInterval[1]
  // Scale for visual bar: range from min(ciLow, p0)-margin to max(ciHigh, p0)+margin
  const margin = Math.max((ciHigh - ciLow) * 0.5, 0.02)
  const rangeMin = Math.max(0, Math.min(ciLow, result.p0) - margin)
  const rangeMax = Math.min(1, Math.max(ciHigh, result.p0) + margin)
  const scale = (v: number) => ((v - rangeMin) / (rangeMax - rangeMin)) * 100

  return (
    <div className="space-y-4">
      {/* Visual confidence interval bar */}
      <div>
        <p className="text-xs font-medium text-muted-foreground mb-2">
          Observed vs Target Bias (with 99% CI)
        </p>
        <div className="relative h-10 rounded-md bg-muted/30 overflow-hidden">
          {/* CI range */}
          <div
            className="absolute top-2 h-6 rounded bg-blue-500/20 border border-blue-500/40"
            style={{
              left: `${scale(ciLow)}%`,
              width: `${scale(ciHigh) - scale(ciLow)}%`,
            }}
          />
          {/* Target p0 marker */}
          <div
            className="absolute top-0 h-full w-0.5 bg-orange-500"
            style={{ left: `${scale(result.p0)}%` }}
          />
          <div
            className="absolute -top-0.5 text-[9px] font-mono text-orange-500 whitespace-nowrap"
            style={{ left: `${scale(result.p0)}%`, transform: 'translateX(-50%)' }}
          >
            p{'\u2080'}={result.p0.toFixed(3)}
          </div>
          {/* Observed pHat marker */}
          <div
            className="absolute top-1.5 h-7 w-1 rounded-sm bg-blue-500"
            style={{ left: `${scale(result.pHat)}%`, transform: 'translateX(-50%)' }}
          />
          <div
            className="absolute bottom-[-2px] text-[9px] font-mono text-blue-500 whitespace-nowrap"
            style={{ left: `${scale(result.pHat)}%`, transform: 'translateX(-50%)' }}
          >
            p̂={result.pHat.toFixed(4)}
          </div>
        </div>
        <div className="flex justify-between mt-1 text-[10px] font-mono text-muted-foreground">
          <span>{rangeMin.toFixed(3)}</span>
          <span>{rangeMax.toFixed(3)}</span>
        </div>
      </div>

      {/* Stats table */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-xs">
        <div>
          <span className="text-muted-foreground">Observed bias (p̂): </span>
          <span className="font-mono">{result.pHat.toFixed(6)}</span>
        </div>
        <div>
          <span className="text-muted-foreground">Target bias (p{'\u2080'}): </span>
          <span className="font-mono">{result.p0.toFixed(6)}</span>
        </div>
        <div>
          <span className="text-muted-foreground">Deviation: </span>
          <span className="font-mono">{result.deviation.toExponential(4)}</span>
        </div>
        <div>
          <span className="text-muted-foreground">Z-statistic: </span>
          <span className="font-mono">{result.zStatistic.toFixed(4)}</span>
        </div>
        <div>
          <span className="text-muted-foreground">95% CI: </span>
          <span className="font-mono break-all">[{ciLow.toFixed(6)}, {ciHigh.toFixed(6)}]</span>
        </div>
        <div>
          <span className="text-muted-foreground">p-value: </span>
          <span className={cn('font-mono', pValueColor(result.pValue))}>{formatPValue(result.pValue)}</span>
        </div>
      </div>
    </div>
  )
}

function BiasStabilityDetails({ result }: { result: BiasStabilityResult }) {
  // Block biases sparkline data
  const blockData = result.blockBiases.map((b, i) => ({ block: i + 1, bias: b }))
  const meanBias = result.blockBiases.reduce((a, b) => a + b, 0) / result.blockBiases.length
  const blockBiasesChartRef = React.useRef<HTMLDivElement>(null)
  const allanVarianceChartRef = React.useRef<HTMLDivElement>(null)

  return (
    <div className="space-y-4">
      {/* Block biases chart */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-1">
            <p className="text-xs font-medium text-muted-foreground">
              Block Biases ({result.blockBiases.length} blocks)
            </p>
            <ChartExportButton containerRef={blockBiasesChartRef} fileName="block-biases" />
          </div>
          <DataExportMenu
            data={blockData}
            fileName="block-biases"
            columns={['block', 'bias']}
            title="Block Biases"
          />
        </div>
        <div ref={blockBiasesChartRef} role="img" aria-label="Block biases chart showing bias variation across data blocks">
        <div className="h-32 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={blockData} margin={{ top: 4, right: 8, bottom: 4, left: 8 }}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted/30" />
              <XAxis
                dataKey="block"
                tick={{ fontSize: 9 }}
                className="text-muted-foreground"
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 9 }}
                className="text-muted-foreground"
                domain={['auto', 'auto']}
                tickLine={false}
              />
              <ReferenceLine
                y={meanBias}
                stroke="hsl(var(--primary))"
                strokeDasharray="4 4"
                strokeWidth={1}
              />
              <Line
                type="monotone"
                dataKey="bias"
                stroke="hsl(210, 100%, 56%)"
                strokeWidth={1}
                dot={false}
              />
              <RechartsTooltip
                contentStyle={{
                  fontSize: 11,
                  background: 'hsl(var(--popover))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: 6,
                  color: 'hsl(var(--popover-foreground))',
                }}
                formatter={(value: any) => [Number(value).toFixed(6), 'Bias']}
                labelFormatter={(label) => `Block ${label}`}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
        </div>
      </div>

      {/* Sub-test results */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
        <div className="rounded-md bg-muted/30 p-3 space-y-1">
          <div className="flex items-center gap-1.5 font-medium">
            <span>Homogeneity Test</span>
            <Badge variant={result.homogeneity.passed ? 'success' : 'destructive'} className="text-[10px] px-1.5 py-0">
              {result.homogeneity.passed ? 'Pass' : 'Fail'}
            </Badge>
          </div>
          <div><span className="text-muted-foreground">Chi-square: </span><span className="font-mono">{result.homogeneity.chiSquare.toFixed(4)}</span></div>
          <div><span className="text-muted-foreground">p-value: </span><span className={cn('font-mono', pValueColor(result.homogeneity.pValue))}>{formatPValue(result.homogeneity.pValue)}</span></div>
          <div><span className="text-muted-foreground">df: </span><span className="font-mono">{result.homogeneity.df}</span></div>
        </div>
        <div className="rounded-md bg-muted/30 p-3 space-y-1">
          <div className="flex items-center gap-1.5 font-medium">
            <span>Trend Test</span>
            <Badge variant={result.trend.passed ? 'success' : 'destructive'} className="text-[10px] px-1.5 py-0">
              {result.trend.passed ? 'Pass' : 'Fail'}
            </Badge>
          </div>
          <div><span className="text-muted-foreground">Slope: </span><span className="font-mono">{result.trend.slope.toExponential(3)}</span></div>
          <div><span className="text-muted-foreground">t-statistic: </span><span className="font-mono">{result.trend.tStatistic.toFixed(4)}</span></div>
          <div><span className="text-muted-foreground">p-value: </span><span className={cn('font-mono', pValueColor(result.trend.pValue))}>{formatPValue(result.trend.pValue)}</span></div>
        </div>
      </div>

      {/* Allan Variance log-log plot */}
      {result.allanVariance.taus.length > 0 && (() => {
        // Build scatter data in log2 space
        const allanPlotData = result.allanVariance.taus.map((tau, i) => {
          const logTau = Math.log2(tau)
          const sv = result.allanVariance.sigmaSquared[i]!
          const logSigma = sv > 0 ? Math.log2(sv) : -50
          return { logTau, logSigma }
        })

        // Reference line with slope = -1 (expected for i.i.d. data)
        // y = -1 * x + c  where c is fitted to pass through the mean of the data
        const meanLogTau = allanPlotData.reduce((s, d) => s + d.logTau, 0) / allanPlotData.length
        const meanLogSigma = allanPlotData.reduce((s, d) => s + d.logSigma, 0) / allanPlotData.length
        const refIntercept = meanLogSigma + meanLogTau // c = meanY - (-1)*meanX = meanY + meanX

        const minLogTau = Math.min(...allanPlotData.map(d => d.logTau))
        const maxLogTau = Math.max(...allanPlotData.map(d => d.logTau))
        const refLineData = [
          { logTau: minLogTau, refLine: -minLogTau + refIntercept },
          { logTau: maxLogTau, refLine: -maxLogTau + refIntercept },
        ]

        return (
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1">
                <p className="text-xs font-medium text-muted-foreground">
                  Allan Variance (log-log)
                </p>
                <ChartExportButton containerRef={allanVarianceChartRef} fileName="allan-variance" />
              </div>
              <DataExportMenu
                data={result.allanVariance.taus.map((tau, i) => ({
                  tau,
                  sigmaSquared: result.allanVariance.sigmaSquared[i],
                  log2Tau: Math.log2(tau),
                  log2Sigma: result.allanVariance.sigmaSquared[i]! > 0 ? Math.log2(result.allanVariance.sigmaSquared[i]!) : undefined,
                }))}
                fileName="allan-variance"
                columns={['tau', 'sigmaSquared', 'log2Tau', 'log2Sigma']}
                title="Allan Variance"
              />
            </div>
            <div ref={allanVarianceChartRef} role="img" aria-label="Allan variance log-log plot showing bias stability across averaging times">
            <div className="h-48 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <ScatterChart margin={{ top: 8, right: 12, bottom: 30, left: 12 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted/30" />
                  <XAxis
                    type="number"
                    dataKey="logTau"
                    name="log\u2082(\u03C4)"
                    domain={['auto', 'auto']}
                    tick={{ fontSize: 9 }}
                    tickLine={false}
                    label={{
                      value: 'log\u2082(\u03C4)',
                      position: 'insideBottom',
                      offset: -18,
                      fontSize: 10,
                      className: 'fill-muted-foreground',
                    }}
                  />
                  <YAxis
                    type="number"
                    dataKey="logSigma"
                    name="log\u2082(\u03C3\u00B2)"
                    domain={['auto', 'auto']}
                    tick={{ fontSize: 9 }}
                    tickLine={false}
                    label={{
                      value: 'log\u2082(\u03C3\u00B2)',
                      angle: -90,
                      position: 'insideLeft',
                      offset: 5,
                      fontSize: 10,
                      className: 'fill-muted-foreground',
                    }}
                  />
                  <RechartsTooltip
                    content={({ active, payload }) => {
                      if (!active || !payload || payload.length < 1) return null
                      const item = payload[0]?.payload as { logTau: number; logSigma: number }
                      if (!item) return null
                      return (
                        <div className="rounded-md border bg-popover px-3 py-2 text-xs shadow-md">
                          <p>
                            <span className="text-muted-foreground">log{'\u2082'}({'\u03C4'}): </span>
                            <span className="font-mono">{item.logTau.toFixed(2)}</span>
                            <span className="text-muted-foreground ml-2">({'\u03C4'} = {Math.round(Math.pow(2, item.logTau))})</span>
                          </p>
                          <p>
                            <span className="text-muted-foreground">log{'\u2082'}({'\u03C3\u00B2'}): </span>
                            <span className="font-mono">{item.logSigma.toFixed(4)}</span>
                          </p>
                        </div>
                      )
                    }}
                  />
                  {/* i.i.d. reference line (slope = -1) */}
                  <ReferenceLine
                    segment={[
                      { x: refLineData[0]!.logTau, y: refLineData[0]!.refLine },
                      { x: refLineData[1]!.logTau, y: refLineData[1]!.refLine },
                    ]}
                    strokeDasharray="6 4"
                    stroke="hsl(var(--muted-foreground))"
                    strokeWidth={1.5}
                  />
                  <Scatter
                    data={allanPlotData}
                    fill="hsl(210, 100%, 56%)"
                    fillOpacity={0.8}
                    r={4}
                  />
                </ScatterChart>
              </ResponsiveContainer>
            </div>
            </div>
            <div className="flex items-center justify-between mt-1 text-[10px] text-muted-foreground">
              <span>Dashed line: slope = -1 (i.i.d. reference)</span>
              <span className="font-mono">Fitted slope: {result.allanVariance.slope.toFixed(4)}</span>
            </div>
          </div>
        )
      })()}

      {/* Allan variance summary info */}
      <div className="flex items-center justify-between text-xs">
        <div>
          <span className="text-muted-foreground">Allan variance slope: </span>
          <span className="font-mono">{result.allanVariance.slope.toFixed(4)}</span>
          <span className="text-muted-foreground ml-2">(ideal: -1.0 for white noise)</span>
        </div>
        <span className="text-muted-foreground font-mono text-[10px]">
          {result.allanVariance.taus.length} octaves
        </span>
      </div>
    </div>
  )
}

function GeneralizedRunsDetails({ result }: { result: GeneralizedRunsResult }) {
  const runsChartRef = React.useRef<HTMLDivElement>(null)
  // Combine observed/expected for chart
  const maxLen = Math.min(result.onesRuns.observed.length, 15)
  const runsData = Array.from({ length: maxLen }, (_, i) => ({
    len: i + 1,
    onesObs: result.onesRuns.observed[i] ?? 0,
    onesExp: result.onesRuns.expected[i] ?? 0,
    zerosObs: result.zerosRuns.observed[i] ?? 0,
    zerosExp: result.zerosRuns.expected[i] ?? 0,
  }))

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
        <div className="rounded-md bg-muted/30 p-3 space-y-1">
          <div className="flex items-center gap-1.5 font-medium">
            <span>Runs of 1s</span>
            <Badge variant={result.onesRuns.passed ? 'success' : 'destructive'} className="text-[10px] px-1.5 py-0">
              {result.onesRuns.passed ? 'Pass' : 'Fail'}
            </Badge>
          </div>
          <div><span className="text-muted-foreground">Chi-square: </span><span className="font-mono">{result.onesRuns.chiSquare.toFixed(4)}</span></div>
          <div><span className="text-muted-foreground">p-value: </span><span className={cn('font-mono', pValueColor(result.onesRuns.pValue))}>{formatPValue(result.onesRuns.pValue)}</span></div>
          <div><span className="text-muted-foreground">df: </span><span className="font-mono">{result.onesRuns.df}</span></div>
        </div>
        <div className="rounded-md bg-muted/30 p-3 space-y-1">
          <div className="flex items-center gap-1.5 font-medium">
            <span>Runs of 0s</span>
            <Badge variant={result.zerosRuns.passed ? 'success' : 'destructive'} className="text-[10px] px-1.5 py-0">
              {result.zerosRuns.passed ? 'Pass' : 'Fail'}
            </Badge>
          </div>
          <div><span className="text-muted-foreground">Chi-square: </span><span className="font-mono">{result.zerosRuns.chiSquare.toFixed(4)}</span></div>
          <div><span className="text-muted-foreground">p-value: </span><span className={cn('font-mono', pValueColor(result.zerosRuns.pValue))}>{formatPValue(result.zerosRuns.pValue)}</span></div>
          <div><span className="text-muted-foreground">df: </span><span className="font-mono">{result.zerosRuns.df}</span></div>
        </div>
      </div>

      {/* Runs distribution chart */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-1">
            <p className="text-xs font-medium text-muted-foreground">Observed vs Expected (1-runs)</p>
            <ChartExportButton containerRef={runsChartRef} fileName="runs-distribution" />
          </div>
          <DataExportMenu
            data={runsData}
            fileName="generalized-runs-distribution"
            columns={['len', 'onesObs', 'onesExp', 'zerosObs', 'zerosExp']}
            title="Generalized Runs Distribution"
          />
        </div>
        <div ref={runsChartRef} role="img" aria-label="Runs distribution bar chart comparing observed vs expected run lengths">
        <div className="h-28 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={runsData} margin={{ top: 4, right: 8, bottom: 4, left: 8 }}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted/30" />
              <XAxis dataKey="len" tick={{ fontSize: 9 }} tickLine={false} />
              <YAxis tick={{ fontSize: 9 }} tickLine={false} />
              <Bar dataKey="onesObs" fill="hsl(210, 100%, 56%)" opacity={0.8} name="Observed" />
              <Bar dataKey="onesExp" fill="hsl(210, 100%, 56%)" opacity={0.3} name="Expected" />
              <RechartsTooltip
                contentStyle={{
                  fontSize: 11,
                  background: 'hsl(var(--popover))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: 6,
                  color: 'hsl(var(--popover-foreground))',
                }}
                labelFormatter={(label) => `Run length ${label}`}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
        </div>
      </div>
    </div>
  )
}

function GeneralizedSerialDetails({ result }: { result: GeneralizedSerialResult }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-xs">
      <div>
        <span className="text-muted-foreground">Pattern length (m): </span>
        <span className="font-mono">{result.patternLength}</span>
      </div>
      <div>
        <span className="text-muted-foreground">Degrees of freedom: </span>
        <span className="font-mono">{result.df}</span>
      </div>
      <div>
        <span className="text-muted-foreground">Chi-square: </span>
        <span className="font-mono">{result.chiSquare.toFixed(4)}</span>
      </div>
      <div>
        <span className="text-muted-foreground">p-value: </span>
        <span className={cn('font-mono', pValueColor(result.pValue))}>{formatPValue(result.pValue)}</span>
      </div>
    </div>
  )
}

function AutocorrelationDetails({ result }: { result: AutocorrelationResult }) {
  const acfChartRef = React.useRef<HTMLDivElement>(null)
  // Show first 50 ACF values as a bar chart
  const maxShow = Math.min(result.acfValues.length, 50)
  const acfData = Array.from({ length: maxShow }, (_, i) => ({
    lag: i + 1,
    acf: result.acfValues[i],
  }))

  return (
    <div className="space-y-4">
      {/* ACF chart */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-1">
            <p className="text-xs font-medium text-muted-foreground">
              Autocorrelation Function (first {maxShow} lags)
            </p>
            <ChartExportButton containerRef={acfChartRef} fileName="acf" />
          </div>
          <DataExportMenu
            data={acfData}
            fileName="acf-values"
            columns={['lag', 'acf']}
            title="Autocorrelation Function"
          />
        </div>
        <div ref={acfChartRef} role="img" aria-label="Autocorrelation function plot showing lag vs correlation coefficient">
        <div className="h-32 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={acfData} margin={{ top: 4, right: 8, bottom: 4, left: 8 }}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted/30" />
              <XAxis dataKey="lag" tick={{ fontSize: 9 }} tickLine={false} />
              <YAxis tick={{ fontSize: 9 }} tickLine={false} domain={['auto', 'auto']} />
              <ReferenceLine y={result.confidenceBand} stroke="hsl(0, 80%, 55%)" strokeDasharray="4 4" strokeWidth={1} />
              <ReferenceLine y={-result.confidenceBand} stroke="hsl(0, 80%, 55%)" strokeDasharray="4 4" strokeWidth={1} />
              <ReferenceLine y={0} stroke="hsl(var(--muted-foreground))" strokeWidth={0.5} />
              <Bar dataKey="acf" name="ACF">
                {acfData.map((entry, idx) => (
                  <Cell
                    key={idx}
                    fill={
                      Math.abs(entry.acf) > result.confidenceBand
                        ? 'hsl(0, 80%, 55%)'
                        : 'hsl(210, 100%, 56%)'
                    }
                    opacity={0.7}
                  />
                ))}
              </Bar>
              <RechartsTooltip
                contentStyle={{
                  fontSize: 11,
                  background: 'hsl(var(--popover))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: 6,
                  color: 'hsl(var(--popover-foreground))',
                }}
                formatter={(value: any) => [Number(value).toFixed(6), 'ACF']}
                labelFormatter={(label) => `Lag ${label}`}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
        </div>
      </div>

      {/* Ljung-Box results */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-medium text-muted-foreground">Ljung-Box Test Results</p>
          <DataExportMenu
            data={result.ljungBox.map((lb) => ({
              lag: lb.lag,
              Q: lb.Q,
              pValue: lb.pValue,
              passed: lb.passed,
            }))}
            fileName="ljung-box-results"
            columns={['lag', 'Q', 'pValue', 'passed']}
            title="Ljung-Box Test Results"
          />
        </div>
        <div className="grid gap-1.5">
          {result.ljungBox.map((lb) => (
            <div key={lb.lag} className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-xs rounded px-2 py-1 bg-muted/20">
              <span className="text-muted-foreground">Lag {lb.lag}</span>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <span className="font-mono">Q = {lb.Q.toFixed(2)}</span>
                <span className={cn('font-mono', pValueColor(lb.pValue))}>p = {formatPValue(lb.pValue)}</span>
                <Badge variant={lb.passed ? 'success' : 'destructive'} className="text-[10px] px-1.5 py-0">
                  {lb.passed ? 'Pass' : 'Fail'}
                </Badge>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="text-xs text-muted-foreground flex flex-wrap gap-x-2">
        <span>Confidence band: <span className="font-mono">{'\u00B1'}{result.confidenceBand.toFixed(4)}</span></span>
        <span>Max lag tested: <span className="font-mono">{result.maxLag}</span></span>
      </div>

      {/* Sampling rate diagnostic */}
      {result.diagnosticNote && (
        <div className="text-xs rounded px-3 py-2 bg-muted/30 border border-border/50">
          <span className="font-medium text-muted-foreground">Sampling diagnostic:</span>{' '}
          <span className="text-foreground/80">{result.diagnosticNote}</span>
        </div>
      )}
    </div>
  )
}

function ConditionalEntropyDetails({ result }: { result: ConditionalEntropyResult }) {
  const entropyChartRef = React.useRef<HTMLDivElement>(null)
  // Build entropy ladder chart
  const ladderData = result.orders.map((order, i) => ({
    order,
    observed: result.entropies[i],
    theoretical: result.theoreticalEntropies[i],
  }))

  return (
    <div className="space-y-4">
      {/* Entropy ladder chart */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-1">
            <p className="text-xs font-medium text-muted-foreground">
              Conditional Entropy Ladder (H(X_n | X_{'{n-1}'}, ..., X_{'{n-k}'}) )
            </p>
            <ChartExportButton containerRef={entropyChartRef} fileName="conditional-entropy" />
          </div>
          <DataExportMenu
            data={ladderData}
            fileName="conditional-entropy-ladder"
            columns={['order', 'observed', 'theoretical']}
            title="Conditional Entropy Ladder"
          />
        </div>
        <div ref={entropyChartRef} role="img" aria-label="Conditional entropy ladder showing observed vs theoretical entropy at each order">
        <div className="h-36 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={ladderData} margin={{ top: 4, right: 8, bottom: 4, left: 8 }}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted/30" />
              <XAxis
                dataKey="order"
                tick={{ fontSize: 9 }}
                label={{ value: 'Order k', position: 'insideBottom', offset: -2, fontSize: 10 }}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 9 }}
                label={{ value: 'H (bits)', angle: -90, position: 'insideLeft', offset: 10, fontSize: 10 }}
                tickLine={false}
              />
              <Line
                type="monotone"
                dataKey="observed"
                stroke="hsl(210, 100%, 56%)"
                strokeWidth={2}
                dot={{ r: 3, fill: 'hsl(210, 100%, 56%)' }}
                name="Observed"
              />
              <Line
                type="monotone"
                dataKey="theoretical"
                stroke="hsl(var(--muted-foreground))"
                strokeWidth={1.5}
                strokeDasharray="5 5"
                dot={{ r: 2 }}
                name="Theoretical"
              />
              <RechartsTooltip
                contentStyle={{
                  fontSize: 11,
                  background: 'hsl(var(--popover))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: 6,
                  color: 'hsl(var(--popover-foreground))',
                }}
                formatter={(value: any, name: any) => [Number(value).toFixed(6) + ' bits', String(name)]}
                labelFormatter={(label) => `Order k=${label}`}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
        </div>
      </div>

      {/* Entropy table */}
      <div>
        <p className="text-xs font-medium text-muted-foreground mb-1">Entropy Values</p>
        <div className="grid gap-1">
          {result.orders.map((order, i) => {
            const diff = result.entropies[i] - result.theoreticalEntropies[i]
            return (
              <div key={order} className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 text-xs rounded px-2 py-1 bg-muted/20">
                <span className="text-muted-foreground font-mono">k={order}</span>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                  <span className="font-mono">H={result.entropies[i].toFixed(6)}</span>
                  <span className="font-mono text-muted-foreground">H*={result.theoreticalEntropies[i].toFixed(6)}</span>
                  <span className={cn('font-mono', Math.abs(diff) < 0.01 ? 'text-green-500' : 'text-yellow-500')}>
                    {'\u0394'}={diff.toFixed(6)}
                  </span>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

function GeneralizedApEnDetails({ result }: { result: GeneralizedApEnResult }) {
  const data = result.patternLengths.map((m, i) => ({
    m,
    observed: result.observedPhi[i],
    theoretical: result.theoreticalPhi[i],
    pValue: result.pValues[i],
    passed: result.passed[i],
  }))

  return (
    <div className="space-y-3">
      <div className="grid gap-1.5">
        {data.map((d) => (
          <div key={d.m} className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-xs rounded px-2 py-1 bg-muted/20">
            <span className="text-muted-foreground font-mono">m={d.m}</span>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <span className="font-mono">phi={d.observed.toFixed(6)}</span>
              <span className="font-mono text-muted-foreground">phi*={d.theoretical.toFixed(6)}</span>
              <span className={cn('font-mono', pValueColor(d.pValue))}>p={formatPValue(d.pValue)}</span>
              <Badge variant={d.passed ? 'success' : 'destructive'} className="text-[10px] px-1.5 py-0">
                {d.passed ? 'Pass' : 'Fail'}
              </Badge>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function SpectralAnalysisDetails({ result }: { result: SpectralAnalysisResult }) {
  const psdChartRef = React.useRef<HTMLDivElement>(null)
  // Build log-log PSD chart data
  const psdChartData = React.useMemo(() => {
    const data: Array<{ logFreq: number; logPSD: number }> = []
    for (let i = 0; i < result.frequencies.length; i++) {
      const f = result.frequencies[i]!
      const p = result.psd[i]!
      if (f > 0 && p > 0) {
        data.push({
          logFreq: Math.log10(f),
          logPSD: Math.log10(p),
        })
      }
    }
    return data
  }, [result])

  // Noise type interpretation
  const alpha = result.spectralExponent
  let noiseLabel: string
  let noiseColor: string
  if (alpha < 0.3) {
    noiseLabel = 'White noise (i.i.d.)'
    noiseColor = 'text-green-500'
  } else if (alpha < 0.7) {
    noiseLabel = 'Pink-ish noise'
    noiseColor = 'text-yellow-500'
  } else if (alpha < 1.3) {
    noiseLabel = '1/f noise (flicker)'
    noiseColor = 'text-orange-500'
  } else if (alpha < 1.7) {
    noiseLabel = 'Brown-ish noise'
    noiseColor = 'text-orange-600'
  } else {
    noiseLabel = 'Random walk (drift)'
    noiseColor = 'text-red-500'
  }

  return (
    <div className="space-y-4">
      {/* PSD log-log chart */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-1">
            <p className="text-xs font-medium text-muted-foreground">
              Power Spectral Density (log-log)
            </p>
            <ChartExportButton containerRef={psdChartRef} fileName="spectral-psd" />
          </div>
          <DataExportMenu
            data={result.frequencies.map((f, i) => ({
              frequency: f,
              psd: result.psd[i],
              log10Freq: f > 0 ? Math.log10(f) : undefined,
              log10PSD: result.psd[i]! > 0 ? Math.log10(result.psd[i]!) : undefined,
            }))}
            fileName="spectral-psd"
            columns={['frequency', 'psd', 'log10Freq', 'log10PSD']}
            title="Power Spectral Density"
          />
        </div>
        <div ref={psdChartRef} role="img" aria-label="Power spectral density log-log plot showing frequency vs PSD">
        <div className="h-48 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={psdChartData} margin={{ top: 8, right: 12, bottom: 30, left: 12 }}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted/30" />
              <XAxis
                dataKey="logFreq"
                type="number"
                domain={['auto', 'auto']}
                tick={{ fontSize: 9 }}
                tickLine={false}
                label={{
                  value: 'log\u2081\u2080(f)',
                  position: 'insideBottom',
                  offset: -18,
                  fontSize: 10,
                  className: 'fill-muted-foreground',
                }}
              />
              <YAxis
                dataKey="logPSD"
                type="number"
                domain={['auto', 'auto']}
                tick={{ fontSize: 9 }}
                tickLine={false}
                label={{
                  value: 'log\u2081\u2080(PSD)',
                  angle: -90,
                  position: 'insideLeft',
                  offset: 5,
                  fontSize: 10,
                  className: 'fill-muted-foreground',
                }}
              />
              <Line
                type="monotone"
                dataKey="logPSD"
                stroke="hsl(280, 70%, 55%)"
                strokeWidth={1}
                dot={false}
              />
              <RechartsTooltip
                contentStyle={{
                  fontSize: 11,
                  background: 'hsl(var(--popover))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: 6,
                  color: 'hsl(var(--popover-foreground))',
                }}
                formatter={(value: any) => [Number(value).toFixed(4), 'log\u2081\u2080(PSD)']}
                labelFormatter={(label) => `log\u2081\u2080(f) = ${Number(label).toFixed(3)}`}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
        </div>
      </div>

      {/* Spectral exponent and interpretation */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="rounded-md bg-muted/30 p-3 space-y-2">
          <p className="text-xs font-medium">Spectral Exponent</p>
          <div className="text-2xl font-mono font-bold">
            {'\u03B1'} = {alpha.toFixed(3)}
          </div>
          <div className="text-xs text-muted-foreground">
            SE = {result.spectralExponentSE.toFixed(4)}
          </div>
          <div className="text-xs">
            <span className="text-muted-foreground">p-value (H{'\u2080'}: {'\u03B1'}=0): </span>
            <span className={cn('font-mono', pValueColor(result.pValue))}>
              {formatPValue(result.pValue)}
            </span>
          </div>
        </div>
        <div className="rounded-md bg-muted/30 p-3 space-y-2">
          <p className="text-xs font-medium">Noise Classification</p>
          <div className={cn('text-lg font-semibold', noiseColor)}>
            {noiseLabel}
          </div>
          <div className="text-[10px] text-muted-foreground space-y-0.5">
            <div>{'\u03B1'} {'\u2248'} 0: White noise (i.i.d.)</div>
            <div>{'\u03B1'} {'\u2248'} 1: 1/f noise (barrier fluctuations)</div>
            <div>{'\u03B1'} {'\u2248'} 2: Random walk (drift)</div>
          </div>
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// T9: Dwell Time Details
// ---------------------------------------------------------------------------

function DwellTimeDetails({ result }: { result: DwellTimeResult }) {
  const histChartRef = React.useRef<HTMLDivElement>(null)

  const chartData0 = React.useMemo(
    () =>
      result.histogram0.map((b) => ({
        bin: b.binCenter,
        observed: b.count,
        expected: b.expectedCount,
      })),
    [result],
  )
  const chartData1 = React.useMemo(
    () =>
      result.histogram1.map((b) => ({
        bin: b.binCenter,
        observed: b.count,
        expected: b.expectedCount,
      })),
    [result],
  )

  return (
    <div className="space-y-4">
      {/* Summary statistics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="rounded-md bg-muted/30 p-3 space-y-2">
          <p className="text-xs font-medium">State 0 (Anti-Parallel)</p>
          <div className="space-y-1 text-xs">
            <div>
              <span className="text-muted-foreground">Mean dwell: </span>
              <span className="font-mono">{result.meanDwell0.toFixed(2)} samples</span>
            </div>
            <div>
              <span className="text-muted-foreground">Median dwell: </span>
              <span className="font-mono">{result.medianDwell0.toFixed(1)} samples</span>
            </div>
            <div>
              <span className="text-muted-foreground">{'\u03C4'}{'\u2080'} (fitted): </span>
              <span className="font-mono">
                {result.tau0.toFixed(3)} {'\u00B1'} {result.tau0_se.toFixed(3)}
              </span>
            </div>
            <div>
              <span className="text-muted-foreground">KS statistic: </span>
              <span className="font-mono">{result.ksStatistic0.toFixed(4)}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Runs: </span>
              <span className="font-mono">{result.state0Runs.length.toLocaleString()}</span>
            </div>
          </div>
          {result.hasHeavyTail0 && (
            <div className="mt-1 rounded bg-yellow-500/10 border border-yellow-500/30 px-2 py-1 text-[10px] text-yellow-600 dark:text-yellow-400">
              Heavy tail detected: 99th percentile exceeds 4.6{'\u03C4'}
            </div>
          )}
        </div>
        <div className="rounded-md bg-muted/30 p-3 space-y-2">
          <p className="text-xs font-medium">State 1 (Parallel)</p>
          <div className="space-y-1 text-xs">
            <div>
              <span className="text-muted-foreground">Mean dwell: </span>
              <span className="font-mono">{result.meanDwell1.toFixed(2)} samples</span>
            </div>
            <div>
              <span className="text-muted-foreground">Median dwell: </span>
              <span className="font-mono">{result.medianDwell1.toFixed(1)} samples</span>
            </div>
            <div>
              <span className="text-muted-foreground">{'\u03C4'}{'\u2081'} (fitted): </span>
              <span className="font-mono">
                {result.tau1.toFixed(3)} {'\u00B1'} {result.tau1_se.toFixed(3)}
              </span>
            </div>
            <div>
              <span className="text-muted-foreground">KS statistic: </span>
              <span className="font-mono">{result.ksStatistic1.toFixed(4)}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Runs: </span>
              <span className="font-mono">{result.state1Runs.length.toLocaleString()}</span>
            </div>
          </div>
          {result.hasHeavyTail1 && (
            <div className="mt-1 rounded bg-yellow-500/10 border border-yellow-500/30 px-2 py-1 text-[10px] text-yellow-600 dark:text-yellow-400">
              Heavy tail detected: 99th percentile exceeds 4.6{'\u03C4'}
            </div>
          )}
        </div>
      </div>

      {/* Switching rate */}
      <div className="rounded-md bg-muted/30 p-3">
        <p className="text-xs font-medium mb-1">Switching Rate</p>
        <div className="text-2xl font-mono font-bold">
          {result.switchingRate.toFixed(4)}
        </div>
        <p className="text-[10px] text-muted-foreground">
          transitions per sample ({(result.switchingRate * 100).toFixed(2)}%)
        </p>
      </div>

      {/* Dwell time histograms */}
      {chartData0.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-1">
              <p className="text-xs font-medium text-muted-foreground">
                Dwell Time Distribution — State 0
              </p>
              <ChartExportButton containerRef={histChartRef} fileName="dwell-time-state0" />
            </div>
            <DataExportMenu
              data={result.histogram0.map((b) => ({
                binCenter: b.binCenter,
                observed: b.count,
                expected: b.expectedCount,
              }))}
              fileName="dwell-time-state0"
              columns={['binCenter', 'observed', 'expected']}
              title="Dwell Time State 0"
            />
          </div>
          <div ref={histChartRef}>
            <div className="h-40 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData0} margin={{ top: 8, right: 12, bottom: 30, left: 12 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted/30" />
                  <XAxis
                    dataKey="bin"
                    type="number"
                    domain={['auto', 'auto']}
                    tick={{ fontSize: 9 }}
                    tickLine={false}
                    label={{
                      value: 'Run length (samples)',
                      position: 'insideBottom',
                      offset: -18,
                      fontSize: 10,
                      className: 'fill-muted-foreground',
                    }}
                  />
                  <YAxis
                    tick={{ fontSize: 9 }}
                    tickLine={false}
                    label={{
                      value: 'Count',
                      angle: -90,
                      position: 'insideLeft',
                      offset: 5,
                      fontSize: 10,
                      className: 'fill-muted-foreground',
                    }}
                  />
                  <Bar dataKey="observed" fill="hsl(200, 70%, 50%)" opacity={0.7} name="Observed" />
                  <Bar dataKey="expected" fill="hsl(0, 70%, 50%)" opacity={0.5} name="Expected (exp)" />
                  <RechartsTooltip
                    contentStyle={{
                      fontSize: 11,
                      background: 'hsl(var(--popover))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: 6,
                      color: 'hsl(var(--popover-foreground))',
                    }}
                    formatter={(value: any, name: any) => [
                      Number(value).toFixed(1),
                      String(name),
                    ]}
                    labelFormatter={(label) => `Bin center: ${Number(label).toFixed(1)}`}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {chartData1.length > 0 && (
        <div>
          <p className="text-xs font-medium text-muted-foreground mb-2">
            Dwell Time Distribution — State 1
          </p>
          <div className="h-40 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData1} margin={{ top: 8, right: 12, bottom: 30, left: 12 }}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted/30" />
                <XAxis
                  dataKey="bin"
                  type="number"
                  domain={['auto', 'auto']}
                  tick={{ fontSize: 9 }}
                  tickLine={false}
                  label={{
                    value: 'Run length (samples)',
                    position: 'insideBottom',
                    offset: -18,
                    fontSize: 10,
                    className: 'fill-muted-foreground',
                  }}
                />
                <YAxis
                  tick={{ fontSize: 9 }}
                  tickLine={false}
                  label={{
                    value: 'Count',
                    angle: -90,
                    position: 'insideLeft',
                    offset: 5,
                    fontSize: 10,
                    className: 'fill-muted-foreground',
                  }}
                />
                <Bar dataKey="observed" fill="hsl(140, 60%, 45%)" opacity={0.7} name="Observed" />
                <Bar dataKey="expected" fill="hsl(0, 70%, 50%)" opacity={0.5} name="Expected (exp)" />
                <RechartsTooltip
                  contentStyle={{
                    fontSize: 11,
                    background: 'hsl(var(--popover))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: 6,
                    color: 'hsl(var(--popover-foreground))',
                  }}
                  formatter={(value: any, name: any) => [
                    Number(value).toFixed(1),
                    String(name),
                  ]}
                  labelFormatter={(label) => `Bin center: ${Number(label).toFixed(1)}`}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Interpretation notes */}
      <div className="rounded-md bg-muted/20 border border-muted/40 p-3 text-[10px] text-muted-foreground space-y-1">
        <div>For memoryless (Bernoulli) switching, dwell times are exponentially distributed.</div>
        <div>Heavy tails indicate correlated dynamics (barrier fluctuations, telegraph noise).</div>
        <div>{'\u03C4'}{'\u2080'}/{'\u03C4'}{'\u2081'} asymmetry reflects energy landscape asymmetry between parallel and anti-parallel states.</div>
        <div>Multiply by sampling interval {'\u0394'}t to convert from samples to physical time.</div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Result card per test
// ---------------------------------------------------------------------------

function TunableBiasResultCard({ result }: { result: TunableBiasTestResult }) {
  const [expanded, setExpanded] = React.useState(false)
  const passed = testPassed(result)
  const stat = primaryStatistic(result)
  const pVal = primaryPValue(result)
  const isInformational = result.testName === 'Conditional Entropy' || result.testName === 'Spectral Analysis' || result.testName === 'Dwell Time'

  const detailsContent = React.useMemo(() => {
    switch (result.testName) {
      case 'Bias Accuracy':
        return <BiasAccuracyDetails result={result as BiasAccuracyResult} />
      case 'Bias Stability':
        return <BiasStabilityDetails result={result as BiasStabilityResult} />
      case 'Generalized Runs':
        return <GeneralizedRunsDetails result={result as GeneralizedRunsResult} />
      case 'Generalized Serial':
        return <GeneralizedSerialDetails result={result as GeneralizedSerialResult} />
      case 'Autocorrelation':
        return <AutocorrelationDetails result={result as AutocorrelationResult} />
      case 'Conditional Entropy':
        return <ConditionalEntropyDetails result={result as ConditionalEntropyResult} />
      case 'Generalized Approximate Entropy':
        return <GeneralizedApEnDetails result={result as GeneralizedApEnResult} />
      case 'Spectral Analysis':
        return <SpectralAnalysisDetails result={result as SpectralAnalysisResult} />
      case 'Dwell Time':
        return <DwellTimeDetails result={result as DwellTimeResult} />
      default:
        return null
    }
  }, [result])

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="text-base">{result.testName}</CardTitle>
          {isInformational ? (
            <Badge variant="secondary">Info</Badge>
          ) : (
            <Badge variant={passed ? 'success' : 'destructive'}>
              {passed ? 'Pass' : 'Fail'}
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <p className="text-xs text-muted-foreground">Statistic</p>
            <p className="font-mono text-sm">{stat.toFixed(6)}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">p-value</p>
            {pVal !== null ? (
              <p className={cn('font-mono text-sm', pValueColor(pVal))}>
                {formatPValue(pVal)}
              </p>
            ) : (
              <p className="font-mono text-sm text-muted-foreground">---</p>
            )}
          </div>
        </div>

        <div className="mt-3">
          <button
            type="button"
            onClick={() => setExpanded(!expanded)}
            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            <ChevronDown
              className={cn(
                'h-3.5 w-3.5 transition-transform',
                expanded && 'rotate-180'
              )}
            />
            {expanded ? 'Hide details' : 'Show details'}
          </button>
          {expanded && (
            <div className="mt-3 rounded-md bg-muted/50 p-4 text-xs">
              {detailsContent}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Progress tracker (local, not using testStore)
// ---------------------------------------------------------------------------

interface LocalProgressProps {
  testNames: string[]
  completedTests: Map<string, TunableBiasTestResult>
  currentTest: string | null
  progress: number
  isRunning: boolean
}

function LocalTestProgress({ testNames, completedTests, currentTest, progress, isRunning }: LocalProgressProps) {
  if (!isRunning && completedTests.size === 0) return null

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">Test Progress</CardTitle>
          <span className="text-sm text-muted-foreground">{Math.round(progress)}%</span>
        </div>
        <Progress value={progress} className="mt-2" />
      </CardHeader>
      <CardContent>
        <ul className="space-y-2">
          {testNames.map((name) => {
            const result = completedTests.get(name)
            let status: 'pending' | 'running' | 'passed' | 'failed' = 'pending'
            if (result) {
              status = testPassed(result) ? 'passed' : 'failed'
              // Conditional Entropy, Spectral Analysis, and Dwell Time are informational
              if (name === 'Conditional Entropy' || name === 'Spectral Analysis' || name === 'Dwell Time') status = 'passed'
            } else if (currentTest === name) {
              status = 'running'
            }

            return (
              <li
                key={name}
                className={cn(
                  'flex items-center justify-between gap-2 rounded-md px-3 py-2 text-sm',
                  status === 'running' && 'bg-primary/5',
                  status === 'pending' && 'text-muted-foreground'
                )}
              >
                <div className="flex items-center gap-2 min-w-0">
                  {status === 'pending' && <div className="h-4 w-4 rounded-full border border-muted-foreground/30" />}
                  {status === 'running' && <Loader2 className="h-4 w-4 animate-spin text-primary" />}
                  {status === 'passed' && <CheckCircle2 className="h-4 w-4 text-green-500" />}
                  {status === 'failed' && <XCircle className="h-4 w-4 text-red-500" />}
                  <span className="truncate">{name}</span>
                </div>
              </li>
            )
          })}
        </ul>
      </CardContent>
    </Card>
  )
}


// ---------------------------------------------------------------------------
// Bias dial indicator
// ---------------------------------------------------------------------------

function BiasIndicator({ value }: { value: number }) {
  // A horizontal bar showing bias from 0 to 1 with marker at value
  const pct = value * 100
  const midDist = Math.abs(value - 0.5)

  // Color gradient: green near 0.5, yellow at moderate bias, orange/red at extremes
  let barColor = 'bg-blue-500'
  if (midDist > 0.3) barColor = 'bg-orange-500'
  else if (midDist > 0.1) barColor = 'bg-yellow-500'

  return (
    <div className="space-y-1.5">
      <div className="relative h-3 rounded-full bg-gradient-to-r from-blue-500/20 via-muted/30 to-blue-500/20 overflow-hidden">
        {/* Center mark at 0.5 */}
        <div className="absolute top-0 left-1/2 h-full w-px bg-muted-foreground/30" />
        {/* Current value indicator */}
        <div
          className={cn('absolute top-0 h-full w-1.5 rounded-full transition-all duration-300', barColor)}
          style={{ left: `calc(${pct}% - 3px)` }}
        />
      </div>
      <div className="flex justify-between text-[10px] font-mono text-muted-foreground">
        <span>0.0</span>
        <span>0.5</span>
        <span>1.0</span>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main Page Component
// ---------------------------------------------------------------------------

export function TunableBiasPage() {
  const persist = usePersistStore()
  const {
    tunableBiasParams: persistedParams,
    tunableBiasResult: persistedResult,
    setTunableBiasParams: persistParams,
    setTunableBiasResult: persistResult,
    clearTunableBiasResult,
  } = persist

  // Per-module input data (persisted)
  const [inputData, setInputDataLocal] = React.useState<import('@/lib/fileParser').ParseResult | null>(
    () => persist.getModuleInput('tunableBias')
  )
  const setInputData = React.useCallback((data: import('@/lib/fileParser').ParseResult) => {
    setInputDataLocal(data)
    persist.setModuleInput('tunableBias', data)
  }, [persist])

  // Parameters (initialize from persist store, fallback to defaults)
  const [params, setParams] = React.useState<TunableBiasParameters>(
    persistedParams ?? { ...DEFAULT_TUNABLE_BIAS_PARAMS },
  )
  const [biasInputText, setBiasInputText] = React.useState(
    (persistedParams?.targetBias ?? DEFAULT_TUNABLE_BIAS_PARAMS.targetBias).toString(),
  )

  // Execution state (local to this page)
  const [isRunning, setIsRunning] = React.useState(false)
  const [currentTest, setCurrentTest] = React.useState<string | null>(null)
  const [progress, setProgress] = React.useState(0)
  const [completedTests, setCompletedTests] = React.useState<Map<string, TunableBiasTestResult>>(new Map())
  const [localSuiteResult, setLocalSuiteResult] = React.useState<TunableBiasSuiteResult | null>(null)
  const suiteResult = localSuiteResult ?? persistedResult ?? null
  const setSuiteResult = React.useCallback((r: TunableBiasSuiteResult | null) => {
    setLocalSuiteResult(r)
    if (r) persistResult(r)
    else clearTunableBiasResult()
  }, [persistResult, clearTunableBiasResult])
  const [error, setError] = React.useState<string | null>(null)

  // Sync params to persist store
  React.useEffect(() => {
    persistParams(params)
  }, [params, persistParams])

  // Restore defaults handler
  const restoreDefaults = React.useCallback(() => {
    setParams({ ...defaultTunableBiasParams })
    setBiasInputText(defaultTunableBiasParams.targetBias.toString())
  }, [])

  // Close results handler
  const handleCloseResults = React.useCallback(() => {
    setSuiteResult(null)
    setCompletedTests(new Map())
    clearTunableBiasResult()
  }, [clearTunableBiasResult])

  // Worker integration
  const workerCallbacks = React.useMemo(
    () => ({
      onProgress: (msg: { testName: string; testIndex: number; totalTests: number; result?: TunableBiasTestResult }) => {
        setCurrentTest(msg.testName)
        setProgress(((msg.testIndex + 1) / msg.totalTests) * 100)
        if (msg.result) {
          setCompletedTests((prev) => {
            const next = new Map(prev)
            next.set(msg.testName, msg.result!)
            return next
          })
        }
      },
      onComplete: (result: TunableBiasSuiteResult) => {
        setSuiteResult(result)
        persistResult(result)
        setIsRunning(false)
        setCurrentTest(null)
        setProgress(100)
      },
      onError: (err: string) => {
        setError(err)
        setIsRunning(false)
        setCurrentTest(null)
      },
    }),
    [persistResult]
  )

  const { run: runWorker, terminate } = useWorker('tunable-bias', workerCallbacks)

  // Handle generated data from DataGenerator
  const handleGeneratedData = React.useCallback(
    (data: Uint8Array | Float64Array, meta: DataGeneratorMeta) => {
      if (!(data instanceof Uint8Array) || !meta.bitLength) return
      setInputData({
        kind: 'binary',
        input: {
          data,
          bitLength: meta.bitLength,
          sampleCount: meta.bitLength,
          fileName: `generated-${meta.description.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()}`,
          fileSize: data.byteLength,
          format: 'binary' as InputFormat,
        },
      })
      // Auto-set target bias if Bernoulli distribution was used
      const pParam = meta.parameters['p']
      if (typeof pParam === 'number' && pParam > 0 && pParam < 1) {
        const clamped = Math.max(0.001, Math.min(0.999, pParam))
        setParams((prev) => ({ ...prev, targetBias: clamped }))
        setBiasInputText(clamped.toString())
      }
      setSuiteResult(null)
      setCompletedTests(new Map())
      setError(null)
    },
    [setInputData],
  )

  // Auto-detect bias when file data is loaded
  const [biasAutoDetected, setBiasAutoDetected] = React.useState(false)
  const handleFileDataLoaded = React.useCallback(
    (result: import('@/lib/fileParser').ParseResult) => {
      if (result.kind !== 'binary') return
      const data = result.input.data
      const n = result.input.bitLength
      let ones = 0
      for (let i = 0; i < n; i++) {
        ones += (data[i >>> 3] >> (7 - (i & 7))) & 1
      }
      const pHat = ones / n
      if (Math.abs(pHat - 0.5) > 0.02) {
        const rounded = Math.round(pHat * 1000) / 1000
        const clamped = Math.max(0.001, Math.min(0.999, rounded))
        setParams((prev) => ({ ...prev, targetBias: clamped }))
        setBiasInputText(clamped.toString())
        setBiasAutoDetected(true)
        setTimeout(() => setBiasAutoDetected(false), 5000)
      }
    },
    []
  )

  // Validate that we have binary data
  const hasBinaryData = inputData?.kind === 'binary'
  const bitLength = hasBinaryData ? inputData.input.bitLength : 0

  // Handle bias slider/input
  const handleBiasChange = React.useCallback((value: number) => {
    const clamped = Math.max(0.001, Math.min(0.999, value))
    setParams((p) => ({ ...p, targetBias: clamped }))
    setBiasInputText(clamped.toString())
  }, [])

  const handleBiasInputChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const text = e.target.value
    setBiasInputText(text)
    const num = parseFloat(text)
    if (!isNaN(num) && num >= 0.001 && num <= 0.999) {
      setParams((p) => ({ ...p, targetBias: num }))
    }
  }, [])

  const handleBiasInputBlur = React.useCallback(() => {
    // Snap to valid value on blur
    const num = parseFloat(biasInputText)
    if (isNaN(num) || num < 0.001 || num > 0.999) {
      setBiasInputText(params.targetBias.toString())
    } else {
      const clamped = Math.max(0.001, Math.min(0.999, num))
      setParams((p) => ({ ...p, targetBias: clamped }))
      setBiasInputText(clamped.toString())
    }
  }, [biasInputText, params.targetBias])

  // Preset handlers
  const applyPreset = React.useCallback((bias: number) => {
    handleBiasChange(bias)
  }, [handleBiasChange])

  // Run analysis
  const handleRun = React.useCallback(() => {
    if (!inputData || inputData.kind !== 'binary') return

    // Instantly jump to top, then state updates — avoids layout-shift jitter
    window.scrollTo(0, 0)

    setIsRunning(true)
    setError(null)
    setSuiteResult(null)
    setCompletedTests(new Map())
    setProgress(0)
    setCurrentTest(null)

    const buffer = inputData.input.data.buffer.slice(
      inputData.input.data.byteOffset,
      inputData.input.data.byteOffset + inputData.input.data.byteLength
    ) as ArrayBuffer

    runWorker(buffer, {
      bitLength: inputData.input.bitLength,
      params: {
        targetBias: params.targetBias,
        stabilityBlocks: params.stabilityBlocks,
        maxLag: params.maxLag,
        serialPatternLength: params.serialPatternLength,
        maxConditionalOrder: params.maxConditionalOrder,
        alpha: params.alpha,
      },
    })
  }, [inputData, params, runWorker])

  // Cancel
  const handleCancel = React.useCallback(() => {
    terminate()
    setIsRunning(false)
    setCurrentTest(null)
  }, [terminate])

  // Build export report
  const exportReport: ExportReport | null = React.useMemo(() => {
    if (!suiteResult || !inputData || inputData.kind !== 'binary') return null
    return {
      version: APP_VERSION,
      timestamp: new Date().toISOString(),
      inputFile: {
        name: inputData.input.fileName,
        size: inputData.input.fileSize,
        sha256: inputData.input.sha256 ?? '',
        sampleCount: inputData.input.sampleCount,
        format: inputData.input.format,
      },
      results: {
        tunableBias: suiteResult,
      },
    }
  }, [suiteResult, inputData])

  return (
    <TooltipProvider>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 overflow-hidden">
        {/* Header */}
        <h1 className="text-2xl font-bold mb-2">
          Tunable-Bias Analysis
          <Badge variant="default" className="text-[10px] ml-2 align-middle">Novel</Badge>
        </h1>
        <p className="text-muted-foreground mb-8">
          Tests{' '}
          <span className="font-mono text-foreground">H₀: P(1) = p₀</span> for arbitrary bias.
          Seven specialized tests generalized for biased Bernoulli sequences.
        </p>

        {/* Main grid layout */}
        <div className="grid lg:grid-cols-[380px_1fr] gap-8">
          {/* ============================================================ */}
          {/* Left Panel — Input & Parameters */}
          {/* ============================================================ */}
          <div className="space-y-6 min-w-0">
            <SectionHeader title="Input" icon={FileText} />

            {/* File Upload */}
            <FileUploader
              acceptedKinds={['binary']}
              formatHints="binary"
              kindMismatchMessage="This module requires binary data. For continuous data, use the Gaussian page with PIT Bridge."
              onDataLoaded={(r) => { handleFileDataLoaded(r); setInputDataLocal(r); persist.setModuleInput('tunableBias', r) }}
              moduleData={inputData}
              onModuleClear={() => { setInputDataLocal(null); persist.clearModuleInput('tunableBias') }}
            />

            {/* Import from other modules */}
            {!inputData && (
              <ImportDataBanner
                currentModule="tunableBias"
                acceptedKinds={['binary']}
                onImport={setInputData}
              />
            )}

            {/* Auto-detect bias notification */}
            {biasAutoDetected && (
              <div className="flex items-center gap-2 rounded-md bg-blue-500/10 border border-blue-500/30 p-3">
                <Info className="h-4 w-4 text-blue-500 shrink-0" />
                <p className="text-xs text-blue-600 dark:text-blue-400">
                  Target bias automatically set to p&#8320; = {params.targetBias.toFixed(3)} based on detected data bias.
                </p>
              </div>
            )}

            {/* OR separator + Data Generator */}
            <div className="relative flex items-center justify-center">
              <Separator className="flex-1" />
              <span className="px-3 text-xs font-medium text-muted-foreground uppercase">
                or
              </span>
              <Separator className="flex-1" />
            </div>

            <DataGenerator mode="binary" onDataGenerated={handleGeneratedData} />

            <SectionHeader title="Parameters" icon={Sliders} />

            {/* Target Bias — THE key parameter */}
            <Card className="border-primary/30 bg-primary/[0.02]">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Target className="h-4 w-4 text-primary" />
                    <CardTitle className="text-base">Target Bias p{'\u2080'}</CardTitle>
                  </div>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button type="button" className="text-muted-foreground hover:text-foreground">
                        <Info className="h-4 w-4" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="left" className="max-w-xs">
                      <p>The expected probability P(bit = 1) for your biased RNG. All tests are calibrated against this value.</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Large value display */}
                <div className="flex items-center gap-3">
                  <div className="flex-1">
                    <Input
                      type="text"
                      inputMode="decimal"
                      value={biasInputText}
                      onChange={handleBiasInputChange}
                      onBlur={handleBiasInputBlur}
                      className="text-center font-mono text-2xl h-14 text-foreground border-primary/30 focus-visible:ring-primary/50"
                      disabled={isRunning}
                    />
                  </div>
                  <div className="text-right">
                    <p className={cn('text-sm font-medium', biasColor(params.targetBias))}>
                      {biasLabel(params.targetBias)}
                    </p>
                    <p className="text-xs text-muted-foreground font-mono">
                      P(0) = {(1 - params.targetBias).toFixed(3)}
                    </p>
                  </div>
                </div>

                {/* Slider */}
                <div>
                  <input
                    type="range"
                    min="0.001"
                    max="0.999"
                    step="0.001"
                    value={params.targetBias}
                    onChange={(e) => handleBiasChange(parseFloat(e.target.value))}
                    disabled={isRunning}
                    className="w-full h-2 rounded-full appearance-none cursor-pointer bg-muted accent-primary disabled:opacity-50"
                  />
                  <BiasIndicator value={params.targetBias} />
                </div>

                {/* Entropy info */}
                <div className="rounded-md bg-muted/50 p-2.5 text-xs">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Shannon entropy:</span>
                    <span className="font-mono">
                      {(
                        -(params.targetBias * Math.log2(params.targetBias) +
                          (1 - params.targetBias) * Math.log2(1 - params.targetBias))
                      ).toFixed(6)}{' '}
                      bits
                    </span>
                  </div>
                </div>

                {/* Preset buttons */}
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1 text-xs"
                    onClick={() => applyPreset(0.5)}
                    disabled={isRunning}
                  >
                    p = 0.5
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1 text-xs"
                    onClick={() => applyPreset(0.7)}
                    disabled={isRunning}
                  >
                    p = 0.7
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1 text-xs"
                    onClick={() => applyPreset(0.9)}
                    disabled={isRunning}
                  >
                    p = 0.9
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Advanced Parameters */}
            <Accordion type="single" collapsible className="rounded-lg border bg-card">
              <AccordionItem value="advanced" className="border-0">
                <AccordionTrigger className="px-4 py-3 hover:no-underline">
                  <div className="flex items-center gap-2 text-sm">
                    <Settings2 className="h-4 w-4 text-muted-foreground" />
                    Advanced Parameters
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 text-[10px] text-muted-foreground ml-auto mr-2"
                      onClick={(e) => { e.stopPropagation(); restoreDefaults() }}
                      disabled={isRunning}
                    >
                      <RotateCcw className="h-3 w-3 mr-1" />
                      Defaults
                    </Button>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-4">
                  <div className="space-y-4">
                    {/* Stability Blocks K */}
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="stabilityBlocks" className="text-xs flex items-center gap-1.5">
                          Stability blocks (K)
                          <TooltipProvider delayDuration={200}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="text-muted-foreground/50 cursor-help">
                                  <Info className="h-3 w-3" />
                                </span>
                              </TooltipTrigger>
                              <TooltipContent side="right" className="max-w-[240px]">
                                <p>Number of equal-length blocks to divide the sequence into for bias stability analysis. More blocks increase sensitivity to short-term drift but reduce per-block sample size. Typical: 100 for 1M bits {'\u2192'} 10K bits per block.</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </Label>
                      </div>
                      <Input
                        id="stabilityBlocks"
                        type="number"
                        min={10}
                        max={1000}
                        value={params.stabilityBlocks}
                        onChange={(e) =>
                          setParams((p) => ({
                            ...p,
                            stabilityBlocks: Math.max(10, Math.min(1000, parseInt(e.target.value) || 100)),
                          }))
                        }
                        disabled={isRunning}
                        className="h-9 font-mono text-sm"
                      />
                    </div>

                    {/* Max ACF Lag */}
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="maxLag" className="text-xs flex items-center gap-1.5">
                          Max ACF lag
                          <TooltipProvider delayDuration={200}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="text-muted-foreground/50 cursor-help">
                                  <Info className="h-3 w-3" />
                                </span>
                              </TooltipTrigger>
                              <TooltipContent side="right" className="max-w-[240px]">
                                <p>Maximum lag {'\u03C4'} for the autocorrelation function. The ACF is computed for all lags from 1 to this value using FFT. Larger values detect longer-range correlations but increase computation time.</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </Label>
                      </div>
                      <Input
                        id="maxLag"
                        type="number"
                        min={10}
                        max={100000}
                        value={params.maxLag}
                        onChange={(e) =>
                          setParams((p) => ({
                            ...p,
                            maxLag: Math.max(10, Math.min(100000, parseInt(e.target.value) || 1000)),
                          }))
                        }
                        disabled={isRunning}
                        className="h-9 font-mono text-sm"
                      />
                    </div>

                    {/* Serial Pattern Length */}
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="serialPatternLength" className="text-xs flex items-center gap-1.5">
                          Serial pattern length (m)
                          <TooltipProvider delayDuration={200}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="text-muted-foreground/50 cursor-help">
                                  <Info className="h-3 w-3" />
                                </span>
                              </TooltipTrigger>
                              <TooltipContent side="right" className="max-w-[240px]">
                                <p>Length of binary patterns for the generalized serial test. Creates 2ᵐ possible patterns. For n = 1M bits, m {'\u2264'} 14 ensures sufficient samples per pattern. Default 8 gives 256 patterns.</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </Label>
                      </div>
                      <Input
                        id="serialPatternLength"
                        type="number"
                        min={2}
                        max={16}
                        value={params.serialPatternLength}
                        onChange={(e) =>
                          setParams((p) => ({
                            ...p,
                            serialPatternLength: Math.max(2, Math.min(16, parseInt(e.target.value) || 8)),
                          }))
                        }
                        disabled={isRunning}
                        className="h-9 font-mono text-sm"
                      />
                    </div>

                    {/* Max Conditional Entropy Order */}
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="maxConditionalOrder" className="text-xs flex items-center gap-1.5">
                          Max conditional entropy order
                          <TooltipProvider delayDuration={200}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="text-muted-foreground/50 cursor-help">
                                  <Info className="h-3 w-3" />
                                </span>
                              </TooltipTrigger>
                              <TooltipContent side="right" className="max-w-[240px]">
                                <p>Maximum context depth k for the conditional entropy ladder H(X{'\u209C'}|X{'\u209C\u208B\u2081'},...,X{'\u209C\u208B\u2096'}). Higher k detects higher-order Markov dependencies. Limited to k {'\u2264'} 12 for n = 1M (need ~1000 samples per 2ᵏ context).</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </Label>
                      </div>
                      <Input
                        id="maxConditionalOrder"
                        type="number"
                        min={1}
                        max={16}
                        value={params.maxConditionalOrder}
                        onChange={(e) =>
                          setParams((p) => ({
                            ...p,
                            maxConditionalOrder: Math.max(1, Math.min(16, parseInt(e.target.value) || 10)),
                          }))
                        }
                        disabled={isRunning}
                        className="h-9 font-mono text-sm"
                      />
                    </div>

                    {/* Significance Level */}
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="alpha" className="text-xs flex items-center gap-1.5">
                          Significance level ({'\u03B1'})
                          <TooltipProvider delayDuration={200}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="text-muted-foreground/50 cursor-help">
                                  <Info className="h-3 w-3" />
                                </span>
                              </TooltipTrigger>
                              <TooltipContent side="right" className="max-w-[240px]">
                                <p>Threshold for pass/fail decisions. A test with p-value {'<'} {'\u03B1'} is considered a failure. Default 0.01 means 1% false positive rate per test on truly random data.</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </Label>
                      </div>
                      <Input
                        id="alpha"
                        type="number"
                        min={0.001}
                        max={0.1}
                        step={0.001}
                        value={params.alpha}
                        onChange={(e) =>
                          setParams((p) => ({
                            ...p,
                            alpha: Math.max(0.001, Math.min(0.1, parseFloat(e.target.value) || 0.01)),
                          }))
                        }
                        disabled={isRunning}
                        className="h-9 font-mono text-sm"
                      />
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>

            {/* Run / Cancel Button */}
            <div className="space-y-3">
              {isRunning ? (
                <Button
                  variant="destructive"
                  size="lg"
                  className="w-full"
                  onClick={handleCancel}
                >
                  <Square className="h-4 w-4" />
                  Cancel Analysis
                </Button>
              ) : (
                <Button
                  size="lg"
                  className="w-full"
                  onClick={handleRun}
                  disabled={!hasBinaryData}
                  data-testid="run-analysis-btn"
                >
                  <Play className="h-4 w-4" />
                  Run Analysis
                </Button>
              )}

              {!hasBinaryData && !isRunning && (
                <p className="text-xs text-center text-muted-foreground">
                  Upload binary data to begin analysis
                </p>
              )}

              {hasBinaryData && !isRunning && (
                <div className="flex items-center justify-center gap-1 text-xs text-muted-foreground">
                  <Zap className="h-3 w-3" />
                  <span>
                    {bitLength.toLocaleString()} bits loaded
                    {bitLength < 10000 && (
                      <span className="text-yellow-500 ml-1">(recommend 10,000+ for reliable results)</span>
                    )}
                  </span>
                </div>
              )}
            </div>

            {/* Error display */}
            {error && (
              <Card className="border-destructive/50">
                <CardContent className="p-4">
                  <p className="text-sm text-destructive">{error}</p>
                </CardContent>
              </Card>
            )}

            {/* Info note */}
            <div className="flex gap-2 rounded-md bg-primary/5 p-3">
              <Info className="h-4 w-4 text-primary shrink-0 mt-0.5" />
              <p className="text-xs text-muted-foreground">
                Requires binary bitstream data with a known target bias P(bit=1). Recommended: 10,000+ bits for reliable results.
              </p>
            </div>
          </div>

          {/* ============================================================ */}
          {/* Right Panel — Results */}
          {/* ============================================================ */}
          <div className="space-y-6 min-w-0">
            <SectionHeader
              title="Results"
              icon={TrendingUp}
              right={
                suiteResult ? (
                  <>
                    {exportReport && <ExportButton report={exportReport} />}
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={handleCloseResults}>
                      <X className="h-4 w-4" />
                    </Button>
                  </>
                ) : undefined
              }
            />

            {/* Progress tracker */}
            {(isRunning || completedTests.size > 0) && !suiteResult && (
              <LocalTestProgress
                testNames={TUNABLE_BIAS_TEST_NAMES}
                completedTests={completedTests}
                currentTest={currentTest}
                progress={progress}
                isRunning={isRunning}
              />
            )}

            {/* Results */}
            {suiteResult && (
              <>
                <ResultSummaryBar
                  passCount={suiteResult.tests.filter((t: TunableBiasTestResult) => t.testName !== 'Conditional Entropy' && t.testName !== 'Spectral Analysis' && t.testName !== 'Dwell Time' && testPassed(t)).length}
                  failCount={suiteResult.tests.filter((t: TunableBiasTestResult) => t.testName !== 'Conditional Entropy' && t.testName !== 'Spectral Analysis' && t.testName !== 'Dwell Time' && !testPassed(t)).length}
                  totalTests={suiteResult.tests.length}
                  infoCount={suiteResult.tests.filter((t: TunableBiasTestResult) => t.testName === 'Conditional Entropy' || t.testName === 'Spectral Analysis' || t.testName === 'Dwell Time').length}
                  overallPassed={suiteResult.tests.filter((t: TunableBiasTestResult) => t.testName !== 'Conditional Entropy' && t.testName !== 'Spectral Analysis' && t.testName !== 'Dwell Time').every((t: TunableBiasTestResult) => testPassed(t))}
                  executionTimeMs={suiteResult.executionTimeMs}
                  passMessage="Data consistent with i.i.d. Bernoulli(p\u2080)"
                  failMessage="Data deviates from i.i.d. Bernoulli(p\u2080)"
                  detail={`p\u2080 = ${suiteResult.parameters.targetBias} \u00B7 \u03B1 = ${suiteResult.parameters.alpha}`}
                />

                <SnapshotManager module="tunableBias" hasResult={!!suiteResult} onLoadSnapshot={(snap) => {
                  setLocalSuiteResult(snap.result)
                }} />

                <TBPValueChart
                  tests={suiteResult.tests}
                  alpha={suiteResult.parameters.alpha}
                />

                {/* Detailed results label */}
                <p className="text-sm text-muted-foreground">
                  Detailed results for each test:
                </p>
              </>
            )}

            {/* Individual test results */}
            {suiteResult && (
              <div className="space-y-4">
                {suiteResult.tests.map((test: TunableBiasTestResult) => (
                  <TunableBiasResultCard key={test.testName} result={test} />
                ))}
              </div>
            )}

            {/* Progressive results while running */}
            {isRunning && completedTests.size > 0 && (
              <div className="space-y-4">
                {Array.from(completedTests.values()).map((test) => (
                  <TunableBiasResultCard key={test.testName} result={test} />
                ))}
              </div>
            )}

            {/* Empty state */}
            {!isRunning && !suiteResult && completedTests.size === 0 && (
              <Card className="border-dashed">
                <CardContent className="p-12">
                  <div className="flex flex-col items-center gap-4 text-center">
                    <div className="rounded-full bg-primary/10 p-4">
                      <Activity className="h-8 w-8 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold mb-1">Ready for Analysis</h3>
                      <p className="text-sm text-muted-foreground max-w-md">
                        Upload binary data and set your target bias{' '}
                        <span className="font-mono">p{'\u2080'}</span> to run the tunable-bias test suite.
                        Results will appear here progressively as each test completes.
                      </p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-2 w-full max-w-lg">
                      <div className="rounded-lg border bg-card/50 p-3 text-left">
                        <div className="flex items-center gap-2 mb-1">
                          <TrendingUp className="h-3.5 w-3.5 text-blue-500" />
                          <p className="text-xs font-medium">T1-T2</p>
                        </div>
                        <p className="text-[10px] text-muted-foreground">Bias accuracy and stability</p>
                      </div>
                      <div className="rounded-lg border bg-card/50 p-3 text-left">
                        <div className="flex items-center gap-2 mb-1">
                          <BarChart3 className="h-3.5 w-3.5 text-green-500" />
                          <p className="text-xs font-medium">T3-T4</p>
                        </div>
                        <p className="text-[10px] text-muted-foreground">Runs and serial patterns</p>
                      </div>
                      <div className="rounded-lg border bg-card/50 p-3 text-left">
                        <div className="flex items-center gap-2 mb-1">
                          <Activity className="h-3.5 w-3.5 text-orange-500" />
                          <p className="text-xs font-medium">T5-T7</p>
                        </div>
                        <p className="text-[10px] text-muted-foreground">ACF, entropy, and ApEn</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </TooltipProvider>
  )
}
